import { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget = 'help_center_search_result' | 'submit'

type SearchFaqEntriesEventArgs = {
  searchSessionId: string | null
  searchQuery: string
  faqEntryIds: Array<number>
  helpCenterSessionId: string
}

type SearchFaqEntriesEventExtra = {
  search_session_id: string | null
  search_query: string
  faq_entry_ids: Array<number>
  help_center_session_id: string
}

export const searchFaqEntriesEvent = (args: SearchFaqEntriesEventArgs) => {
  const extra: SearchFaqEntriesEventExtra = {
    search_session_id: args.searchSessionId,
    search_query: args.searchQuery,
    faq_entry_ids: args.faqEntryIds,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.search_faq_entries',
    extra,
  }
}

type ViewFaqEntryArgs = {
  faqEntryId: number
  faqEntryChannel: string
  faqAccessChannel: string | null
  screen: string
  helpCenterSessionId: string
}

type ViewFaqEntryExtra = {
  faq_entry_id: number
  faq_access_channel: string | null
  faq_entry_channel: string
  screen: string
  help_center_session_id: string
}

export const viewFaqEntry = (args: ViewFaqEntryArgs) => {
  const extra: ViewFaqEntryExtra = {
    faq_entry_id: args.faqEntryId,
    faq_entry_channel: args.faqEntryChannel,
    faq_access_channel: args.faqAccessChannel,
    screen: args.screen,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.view_faq_entry',
    extra,
  }
}

type userClickFaqRelatedQuestionEventArgs = {
  helpCenterSessionId: string | null
  initialFaqId?: number
  targetFaqId: number
  faqAccessChannel?: string
  faqEntryChannel?: string
}

export const userClickFaqRelatedQuestionEvent = (
  args: userClickFaqRelatedQuestionEventArgs,
): TrackingEvent => {
  const { helpCenterSessionId, initialFaqId, targetFaqId, faqEntryChannel, faqAccessChannel } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
    initial_faq_id: initialFaqId,
    target_faq_id: targetFaqId,
    faq_entry_channel: faqEntryChannel,
    faq_access_channel: faqAccessChannel,
  }

  return {
    event: 'user.click_faq_related_question',
    extra,
  }
}

type userClickRecommendedTopicEventArgs = {
  helpCenterSessionId: string | null
}

export const userClickRecommendedTopicEvent = (
  args: userClickRecommendedTopicEventArgs,
): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.click_recommended_topic',
    extra,
  }
}

type userClickHelpCenterSearchResultArgs = {
  helpCenterSessionId: string | null
}

export const userClickHelpCenterSearchResult = (
  args: userClickHelpCenterSearchResultArgs,
): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.click_help_center_search_result',
    extra,
  }
}

export enum FaqFeedback {
  Positive = 'positive',
  Negative = 'negative',
}

type submitFaqFeedbackEventArgs = {
  faqId: number
  helpCenterSessionId: string | null
  faqAccessChannel?: string
  faqEntryChannel?: string
  faqFeedback: FaqFeedback
}

export const userSubmitFaqFeedbackEvent = (args: submitFaqFeedbackEventArgs): TrackingEvent => {
  const { faqId, helpCenterSessionId, faqEntryChannel, faqAccessChannel, faqFeedback } = args
  const extra = {
    faq_id: faqId,
    help_center_session_id: helpCenterSessionId,
    faq_entry_channel: faqEntryChannel,
    faq_access_channel: faqAccessChannel,
    faq_feedback: faqFeedback,
  }

  return {
    event: 'user.submit_faq_feedback',
    extra,
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}
